import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin } from "lucide-react";
import { Reveal, MaskLines } from "./Motion";
import { SITE, IMG } from "@/lib/site";

const VALUES = [
  ["Préparation minutieuse", "80 % d'un beau résultat se joue avant le premier coup de rouleau."],
  ["Chantier protégé & propre", "Bâches, adhésifs, aspiration quotidienne : votre maison reste habitable."],
  ["Matériaux premium", "Peintures professionnelles et écolabellisées, sélectionnées pour durer."],
  ["Un seul interlocuteur", "Le même artisan du devis à la réception des travaux."],
];

export const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <section ref={ref} id="a-propos" data-testid="about-section" className="relative bg-paper px-5 py-24 sm:px-8 lg:py-32 border-t border-paper-line">
      <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12 lg:gap-20">
        <Reveal className="lg:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden">
            <motion.img src={IMG.painter} alt="Artisan peintre au travail" style={{ y, scale: 1.2 }} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute bottom-0 left-0 bg-ink p-6 text-paper">
              <p className="font-display text-5xl">{SITE.founded}</p>
              <p className="text-xs text-paper/80 mt-1">Fondation de l'atelier</p>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-7 lg:pt-10">
          <p className="eyebrow text-gold-deep mb-6">L'atelier</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            <MaskLines lines={["Le soin d'un artisan,", <em key="e" className="text-clay">la rigueur d'une entreprise.</em>]} />
          </h2>
          <Reveal className="mt-8 space-y-5 text-base sm:text-lg leading-relaxed text-slate-700 max-w-2xl">
            <p>
              {SITE.name} est une entreprise artisanale de peinture et de décoration installée à {SITE.city}, dirigée par {SITE.owner}. Depuis {SITE.founded}, nous accompagnons particuliers, copropriétés et professionnels dans la rénovation de leurs intérieurs comme de leurs façades.
            </p>
            <p>
              Notre conviction : un travail bien fait se voit dans les détails. Des angles nets, des aplats sans reprise, un chantier rendu plus propre qu'à notre arrivée.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-px bg-paper-line sm:grid-cols-2 border border-paper-line">
            {VALUES.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.06} className="bg-paper p-6" testId={`about-value-${i}`}>
                <span className="font-mono text-xs text-gold-deep">0{i + 1}</span>
                <h3 className="mt-3 font-display text-2xl">{t}</h3>
                <p className="mt-2 text-sm text-slate-600">{d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const Zone = () => (
  <section id="zone" data-testid="zone-section" className="relative overflow-hidden bg-paper-deep px-5 py-24 sm:px-8 lg:py-32">
    <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12 lg:items-center">
      <div className="lg:col-span-5">
        <p className="eyebrow text-gold-deep mb-6">Zone d'intervention</p>
        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
          <MaskLines lines={[SITE.city, <em key="e" className="text-clay">et {SITE.radius} alentour.</em>]} />
        </h2>
        <Reveal className="mt-6 max-w-md text-slate-700">
          Au départ d'Angers, nous intervenons dans tout le Maine-et-Loire et jusqu'à {SITE.radius} : Loire-Atlantique, Mayenne, Indre-et-Loire. Visite technique et devis gratuits, et au-delà sur demande.
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <ul className="flex flex-wrap gap-2 sm:hidden" data-testid="zone-communes-mobile">
          {SITE.communes.map((c) => <li key={c} className="border border-paper-line bg-paper px-3 py-1.5 text-xs font-medium">{c}</li>)}
        </ul>
        <Reveal className="relative mx-auto hidden aspect-square w-full max-w-[560px] sm:block">
          {[1, 0.5].map((s, k) => (
            <span key={k} className="absolute rounded-full border border-ink/15" style={{ inset: `${(1 - s) * 44 + 6}%` }}>
              <span className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 bg-paper-deep px-2 font-mono text-[10px] text-slate-600">{k ? "50 km" : "100 km"}</span>
            </span>
          ))}
          <span className="absolute inset-[40%] rounded-full bg-gold/25 animate-ping [animation-duration:3s]" />
          <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-ink text-gold" data-testid="zone-center">
            <MapPin className="h-6 w-6" />
          </span>
          <span className="absolute left-1/2 top-[calc(50%+44px)] -translate-x-1/2 font-display text-xl">Angers</span>
          {SITE.mainCities.map((c) => {
            const a = (c.deg * Math.PI) / 180;
            const r = Math.min(c.km, 100) * 0.44;
            return (
              <span key={c.name} data-testid={`zone-city-${c.name.toLowerCase()}`} className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
                style={{ left: `${50 + r * Math.cos(a)}%`, top: `${50 + r * Math.sin(a)}%` }}>
                <span className="h-2.5 w-2.5 rounded-full bg-clay ring-4 ring-clay/20" />
                <span className="mt-2 whitespace-nowrap bg-ink px-3 py-1 text-xs font-semibold text-paper">{c.name}</span>
                <span className="mt-1 font-mono text-[10px] text-slate-600">{c.km} km</span>
              </span>
            );
          })}
        </Reveal>
      </div>
    </div>
  </section>
);
